import { VoiceFlowContextSnapshot } from './flows/voice-flow.models';
import type { RenderOutputSettings } from '../../render-runtime';

export type VoiceAssistantLanguage = 'en' | 'hi';
export type VoiceAssistantMode = 'work' | 'talk';

export type AssistantResolveStatus = 'matched' | 'partial' | 'unsupported' | 'error';

export type AssistantActionId =
  | 'navigate_route'
  | 'open_template_studio_video'
  | 'open_smart_video_create'
  | 'open_processing_jobs'
  | 'open_review_pipeline'
  | 'open_trends'
  | 'open_trend_create'
  | 'open_social_post_create'
  | 'smart-video:compose'
  | 'byok_test_keys'
  | 'close_active_dialog'
  | 'explain_current_workflow'
  | 'cancel_guided_workflow'
  | 'confirm_generate_smart_video';

export const assistantActionIds: AssistantActionId[] = [
  'navigate_route',
  'open_template_studio_video',
  'open_smart_video_create',
  'open_processing_jobs',
  'open_review_pipeline',
  'open_trends',
  'open_trend_create',
  'open_social_post_create',
  'smart-video:compose',
  'byok_test_keys',
  'close_active_dialog',
  'explain_current_workflow',
  'cancel_guided_workflow',
  'confirm_generate_smart_video'
];

export interface AssistantAction {
  id: AssistantActionId | string;
  label?: string;
  payload?: Record<string, unknown>;
  confidence?: number;
  requiresConfirmation?: boolean;
  reason?: string;
}

export interface AssistantResolveCandidate {
  actionId?: string;
  confidence?: number;
  status?: 'proposed' | 'selected' | 'rejected';
  reason?: string;
}

export interface AssistantContext {
  currentPath: string;
  routePath: string;
  queryParams: Record<string, string | number | boolean>;
  mode?: VoiceAssistantMode;
  flowContext?: VoiceFlowContextSnapshot;
  pendingAction?: AssistantPendingAction | null;
}

export interface AssistantResolveRequest {
  message: string;
  route: string;
  language: VoiceAssistantLanguage;
  context: AssistantContext;
}

export interface AssistantResolveResponse {
  status: AssistantResolveStatus;
  replyText?: string;
  language?: string;
  actions: AssistantAction[];
  suggestions?: string[];
  candidates?: AssistantResolveCandidate[];
  pendingAction?: AssistantPendingAction;
  error?: string;
}

export interface AssistantPendingAction {
  actionId: string;
  collectedSlots?: Record<string, unknown>;
  missingSlots?: string[];
  promptText?: string;
}

export interface SmartVideoDraftPayload {
  inputType?: 'Topic' | 'Script' | 'Style';
  inputData?: string;
  nichePresetId?: string;
  autoReview?: boolean;
  durationSeconds?: number;
  sceneCount?: number;
  aiImageCount?: number;
  aiVideoCount?: number;
  timelineBlocks?: Array<{
    kind: 'image' | 'video';
    durationSeconds: number;
  }>;
  featuredCharacterIds?: string[];
  featuredEnvironmentIds?: string[];
  featuredProductIds?: string[];
  enableCharacterDialogue?: boolean;
  dialogueTtsMode?: 'NarratorOnly' | 'MultiVoice';
  scriptSystemPrompt?: string;
  scriptTemperature?: number;
  imageStyle?: string;
  imageStylePromptEnhancement?: string;
  aspectRatio?: string;
  imageResolution?: string;
  imageAspectRatio?: string;
  imageQuality?: string;
  imageOutputFormat?: string;
  generateShorts?: boolean;
  generateThumbnails?: boolean;
  shortsCount?: number;
  selectedSceneIndexes?: number[];
  useTts?: boolean;
  useBrowserTts?: boolean;
  browserTtsVoiceUri?: string;
  ttsVoiceId?: string;
  useAiVideoGeneration?: boolean;
  aiVideoMode?: 'TextToVideo' | 'ImageToVideo';
  aiVideoIntensity?: 'AllScenes' | 'KeyScenesOnly' | 'EveryNthScene';
  videoResolution?: string;
  videoAspectRatio?: string;
  videoQuality?: string;
  videoDurationSeconds?: number;
  videoOutputFormat?: string;
  providerSelectionMode?: 'Auto' | 'Manual';
  scriptProvider?: string;
  scriptModel?: string;
  imageProvider?: string;
  imageModel?: string;
  ttsProvider?: string;
  ttsModel?: string;
  videoProvider?: string;
  videoModel?: string;
  renderOutput?: RenderOutputSettings | null;
  source?: string;
  createdAt?: string;
}

export interface VoiceActionRunResult {
  handled: boolean;
  spokenText?: string;
  executedActionIds: string[];
  unsupportedActionIds: string[];
  errors: string[];
}
